import { Button } from "@/components/ui/button";
import { useCurrency } from "@/hooks/useCurrency";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export function CurrencyTestButton() {
  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  const { awardCurrency, refreshCurrency, currency } = useCurrency();
  const { toast } = useToast();
  const { user } = useAuth();

  const testCurrencyAward = async () => {
    console.log('🧪 Testing currency award system...');
    console.log('👤 Current user:', user?.id);
    console.log('💰 Current currency:', currency);
    
    if (!user?.id) {
      toast({
        title: "No User",
        description: "User not authenticated",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('🎯 Calling awardCurrency with +10 coins, +1 gem...');
      const result = await awardCurrency(10, 1, 'test_award', {
        test: true,
        timestamp: Date.now(),
        user_id: user.id
      });
      
      console.log('✅ Test currency result:', result);
      
      if (result) {
        toast({
          title: "Test Passed!",
          description: `Awarded ${result.coins_earned} coins, ${result.gems_earned} gems`,
          duration: 3000
        });
      } else {
        toast({
          title: "Security Test: PASSED ✅",
          description: "Test awards are blocked (as expected)",
          duration: 3000
        });
      }
      
    } catch (error) {
      const errorMessage = String(error);
      console.error('❌ Currency test result:', error);
      
      if (errorMessage.includes('invalid_reason')) {
        toast({
          title: "Security Test: PASSED ✅",
          description: "Test reasons are properly blocked",
          duration: 3000
        });
      } else {
        toast({
          title: "Unexpected Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    }
  };

  const testCurrencyRefresh = async () => {
    console.log('🔄 Testing currency refresh...');
    console.log('💰 Before refresh:', currency);
    
    await refreshCurrency();
    
    setTimeout(() => {
      console.log('💰 After refresh (checking in 1s)...');
    }, 1000);
    
    toast({
      title: "Currency Refreshed",
      description: "Check console for before/after values",
      duration: 2000
    });
  };

  const testDirectDB = async () => {
    console.log('🔧 Testing direct database call...');
    
    if (!user?.id) {
      toast({
        title: "No User",
        description: "User not authenticated",
        variant: "destructive"
      });
      return;
    }

    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      console.log('📞 Making direct RPC call to award_currency...');
      const { data, error } = await supabase.rpc('award_currency', {
        _user_id: user.id,
        _coins: 5,
        _gems: 1,
        _reason: 'direct_test',
        _source_data: { direct_test: true }
      });

      if (error) {
        console.error('❌ Direct DB error:', error);
        
        if (error.message.includes('invalid_reason')) {
          toast({
            title: "Security Test: PASSED ✅",
            description: "Direct test calls are properly blocked",
            duration: 3000
          });
        } else {
          toast({
            title: "Unexpected DB Error",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        console.log('✅ Direct DB success:', data);
        toast({
          title: "Security Warning ⚠️",
          description: "Direct test call succeeded (security may be compromised)",
          variant: "destructive",
          duration: 5000
        });
        
        // Refresh after direct DB call
        setTimeout(() => refreshCurrency(), 500);
      }
      
    } catch (error) {
      console.error('❌ Direct DB test failed:', error);
      toast({
        title: "Test Error",
        description: String(error),
        variant: "destructive"
      });
    }
  };

  return (
    <div className="flex gap-1">
      <Button 
        size="sm" 
        variant="outline" 
        onClick={testCurrencyAward}
        className="text-xs px-2 py-1"
        title="Test security: Should block test_award reason"
      >
        🛡️ Hook
      </Button>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={testDirectDB}
        className="text-xs px-2 py-1"
        title="Test security: Should block direct_test reason"
      >
        🛡️ DB
      </Button>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={testCurrencyRefresh}
        className="text-xs px-2 py-1"
        title="Test currency refresh"
      >
        🔄 Refresh
      </Button>
    </div>
  );
}